import Card from 'components/card';
import SearchSelector from 'components/fields/SearchSelector';
import SearchIcon from 'components/icons/SearchIcon';
import { getCustomers } from 'interfaces/customer';
import { useEffect, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const getColor = (margin: number): string => {
    if (margin < 5) return '#d73027'; // Very risky
    if (margin < 15) return '#fc8d59'; // At risk
    if (margin < 30) return '#fee08b'; // Medium
    if (margin < 50) return '#91cf60'; // Good
    return '#1a9850'; // Excellent
};

const DownloadCSVButton = ({
    data,
    filename = 'export-marges.csv',
}: {
    data: any[];
    filename?: string;
}) => {
    const downloadCSV = () => {
        if (!data || data.length === 0) return;

        const csvHeaders = Object.keys(data[0]).join(';');
        const csvRows = data.map((row) =>
            Object.values(row)
                .map((value) => {
                    const stringValue = String(value ?? '');
                    const escaped = stringValue.replace(/"/g, '""');
                    return `"${escaped}"`;
                })
                .join(';'),
        );

        const csvContent = [csvHeaders, ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div
            onClick={downloadCSV}
            className="absolute right-4 top-4 cursor-pointer rounded-full bg-lightPrimary p-2 text-gray-600 hover:bg-gray-200 dark:text-white dark:hover:bg-gray-700"
        >
            <FaFileCsv size={20} />
        </div>
    );
};

const ListMargin = (props: { data: any }) => {
    const { data } = props;
    const [period, setPeriod] = useState('descendant');
    const nav = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [customerId, setCustomerId] = useState('all');
    const [customers, setCustomers] = useState<any>([]);

    const filteredData = (data: any) => {
        let filteredData = data.filter((item: any) => {
            return (
                item.ref.toLowerCase().includes(searchQuery) ||
                item.name.toLowerCase().includes(searchQuery)
            );
        });
        if (customerId !== 'all') {
            filteredData = filteredData.filter(
                (item: any) => item.customer === customerId,
            );
        }
        return filteredData;
    };

    const filteredCustomersWithOrders = (customers: any) => {
        if (!data || !data.length) return [];
        const customerIds = data.map((item: any) => item.customer);
        return customers.filter((customer: any) =>
            customerIds.includes(customer._id),
        );
    };

    const initCustomers = async () => {
        const res = await getCustomers();
        if (res) setCustomers(res.data);
    };

    useEffect(() => {
        if (!data || !data.length) return;
        if (period === 'descendant') {
            data.sort((a: any, b: any) => b.percentage - a.percentage);
        }
        if (period === 'ascendant') {
            data.sort((a: any, b: any) => a.percentage - b.percentage);
        }
    }, [period]);

    useEffect(() => {
        initCustomers();
    }, []);

    return (
        <Card
            extra={
                'w-full p-5 h-fit bg-blue-200 h-[320px] shadow shadow-3xl border ma-h'
            }
        >
            <p className="mb-2 text-base font-bold text-navy-700 dark:text-white">
                Marges par produit
            </p>
            <p className="mb-2 text-sm font-normal text-gray-600 dark:text-white">
                Liste des marges par sur les 12 derniers mois.
            </p>
            <DownloadCSVButton data={data} />
            <div className="flex gap-2">
                <div className="flex h-[35px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Produit ou référence..."
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-[450px] text-sm block rounded-full bg-lightPrimary text-navy-700 outline-none"
                    />
                </div>
                {/* <div className="">
                    <SearchSelector
                        displayKey="company"
                        options={[
                            { _id: 'all', company: 'Tous les clients' },
                            ...(filteredCustomersWithOrders(customers) || []),
                        ]}
                        value={customerId}
                        onSelect={(e: any) => {
                            setCustomerId(e._id);
                        }}
                        inputSmall={true}
                    />
                </div> */}
            </div>
            <div className="absolute right-[70px] top-4">
                <div className="flex mt-2 gap-4">
                    <p
                        className={`text-sm ${
                            period === 'descendant'
                                ? 'text-toola-500 underline'
                                : ''
                        } cursor-pointer`}
                        onClick={() => setPeriod('descendant')}
                    >
                        Décroissant
                    </p>
                    <p
                        className={`text-sm ${
                            period === 'ascendant'
                                ? 'text-toola-500 underline'
                                : ''
                        } cursor-pointer`}
                        onClick={() => setPeriod('ascendant')}
                    >
                        Croissant
                    </p>
                </div>
            </div>
            {data ? (
                data.length > 0 ? (
                    <div className="mt-2 w-full h-[220px] overflow-y-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left h-[24px]">
                                    <th
                                        className="pl-[10px] w-[50%] sm:w-[35%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                        rowSpan={2}
                                    >
                                        Produit
                                    </th>
                                    <th
                                        className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                        rowSpan={2}
                                    >
                                        P. d'achat moyen (€)
                                    </th>
                                    <th
                                        className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                        rowSpan={2}
                                    >
                                        P. de vente moyen (€)
                                    </th>
                                    <th
                                        className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                        rowSpan={2}
                                    >
                                        Marge moyenne (€)
                                    </th>
                                    <th
                                        className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                        rowSpan={2}
                                    >
                                        T. Marque moyen (%)
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData(data).map(
                                    (data: any, index: number) => {
                                        return (
                                            <tr
                                                key={index}
                                                className="text-left h-[24px] border-t border-[#E9EDF7] sm:border-none"
                                            >
                                                <td className="text-sm font-medium pl-[10px] text-toola-800">
                                                    <p
                                                        className="flex gap-2 text-toola-400 cursor-pointer hover:underline"
                                                        onClick={() =>
                                                            nav(
                                                                `/update-product/${data._id}`,
                                                            )
                                                        }
                                                    >
                                                        {data.name
                                                            ? data.name.toLowerCase()
                                                            : 'N/A'}{' '}
                                                    </p>
                                                    <div className="sm:hidden font-normal">
                                                        {data.sum +
                                                            ' ' +
                                                            data.unity}
                                                        <br />
                                                        {data.totalPrice}€
                                                    </div>
                                                </td>
                                                <td className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800">
                                                    {data.averagePurchasePrice?.toFixed(
                                                        2,
                                                    )}
                                                    €
                                                </td>
                                                <td className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800">
                                                    {data.averageSellPrice?.toFixed(
                                                        2,
                                                    )}
                                                    €
                                                </td>
                                                <td className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800">
                                                    {data.marginPerUnit?.toFixed(
                                                        2,
                                                    )}
                                                    €
                                                </td>
                                                <td
                                                    className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800"
                                                    style={{
                                                        color:
                                                            data.percentage !=
                                                            null
                                                                ? getColor(
                                                                      data.percentage,
                                                                  )
                                                                : '#6b7280',
                                                    }}
                                                >
                                                    {data.percentage
                                                        ? data.percentage?.toFixed(
                                                              2,
                                                          )
                                                        : 'N/A'}{' '}
                                                    %
                                                </td>
                                            </tr>
                                        );
                                    },
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="w-full flex items-center justify-center h-[50px]">
                        <p className="text-gray-700">
                            Aucun produit à livrer pour cette date
                        </p>
                    </div>
                )
            ) : (
                <div>
                    <p>Chargement...</p>
                </div>
            )}
        </Card>
    );
};

export default ListMargin;
